<template>
  <div>
    <div v-if="submission">
      <h1 class="mb-4 text-xl font-bold">Kiitos hakemuksesta!</h1>

      <div class="mb-4 space-y-2">
        <p v-if="payload.sendEmail">
          Lähetämme yhteenvedon antamaasi sähköpostiosoitteeseen (tarkista myös
          roskapostikansiosi).
        </p>
        <p>Hakemusten käsittelyvaiheessa emme vastaa tiedusteluihin.</p>
        <p>
          Jos voimme tarjota asuntoa, lähetämme asuntotarjouksen ja pyydämme
          toimittamaan tarvittavat liitteet.
        </p>
        <p>
          Kaikkien hakijoiden luottotiedot tarkistetaan Suomen Asiakastieto Oy:n
          tietokannasta.
        </p>
        <p>
          Hakemus on voimassa 3 kuukautta. Jos annetut tiedot muuttuvat, tulee
          tehdä uusi hakemus.
        </p>
      </div>

      <a class="font-bold text-kas-500" href="/">Takaisin etusivulle</a>
    </div>
    <div v-else>
      <div v-if="options" class="lg:flex">
        <div class="flex-1 flex-grow">
          <div v-if="currentPage === pages.intro.value">
            <header class="p-8 mb-8 text-white bg-kas-500">
              <h1 class="text-2xl font-bold">Vuokra-asuntohakemus</h1>
              <p>
                Voit jättää hakemuksen kiinnostavasta asunnosta tai
                yleishakemuksena, jolla voi hakea yhden kunnan alueella
                sijaitsevia asuntoja. Mikäli haet asuntoa useammalta
                paikkakunnalta, tulee sinun täyttää jokaista paikkakuntaa
                koskien oma hakemus. Hakemus on voimassa 3 kuukautta.
              </p>
            </header>
            <div class="p-4 space-y-6">
              <h2 class="text-3xl font-bold">Hyvä tietää</h2>

              <h3 class="text-xl font-bold">
                Vuokra-asuntohakemuksen käsittely
              </h3>
              <p>
                Asuntohakemuksesi kirjautuu asiakasrekisteriimme. Tarkistamme
                kaikkien hakijoiden luottotiedot Suomen Asiakastieto Oy:n
                tietokannasta. Hakemusten käsittelyvaiheessa emme vastaa
                tiedusteluihin. Jos voimme tarjota asuntoa, lähetämme
                asuntotarjouksen.
              </p>

              <h3 class="text-xl font-bold">Asunnon näyttö</h3>
              <p>
                Ennen tarjouksen hyväksymistä on hyvä käydä tutustumassa sinulle
                tarjottuun asuntoon. Asuntotarjouksen yhteydessä ohjeistamme
                asuntoon tutustumisessa.
              </p>

              <h3 class="text-xl font-bold">Asuntohakemuksen liitteet</h3>
              <p>
                Jos toiveesi koskee asuntoa, jonka asukasvalinta tehdään ARA:n
                asukasvalintaperustein, pyydämme sinulta selvityksiä tuloista ja
                varallisuudesta, lainoista, opiskelusta jne.
                Vuokrausneuvottelijamme opastavat sinua liitteiden
                toimittamisessa.
              </p>

              <p class="font-bold">Täyttämiseen tarvitset:</p>

              <ul class="list-disc list-inside">
                <li>
                  Kaikkien asumaan tulevien henkilötiedot, sis. henkilötunnus
                </li>
                <li>
                  Sopimuksen osapuolten tiedot tuloista ja varallisuudesta
                </li>
              </ul>

              <p>*-merkki tarkoittaa pakollisuutta.</p>

              <div class="flex items-center py-4">
                <button
                  class="w-full px-6 py-2 ml-auto font-bold text-white bg-green-700 rounded hover:bg-opacity-80 lg:w-auto"
                  @click="selectPage('apartmentDetails')"
                >
                  Aloita hakemuksen täyttäminen
                </button>
              </div>
            </div>
          </div>

          <div v-if="currentPage === pages.apartmentDetails.value">
            <header class="p-8 mb-8 text-white bg-kas-500">
              <h1 class="text-2xl font-bold">Haettavat kohteet/asunnot</h1>
              <p>
                Aloita valitsemalla paikkakunta ja valitse tarjottaviin
                asuntoihin liittyvät toiveet. Voit myös hakea tiettyjä asuntoja
                hakemalla niitä vuokra-asunnot sivultamme ja lisäämällä ne
                hakemukseen.
              </p>
            </header>

            <Form
              class="px-8"
              v-slot="{ errors }"
              :validation-schema="apartmentDetailsSchema"
              @submit="submitApartmentDetails"
            >
              <div class="mb-4 border-b">
                <Radio
                  name="applicationTypeId"
                  :options="options.applicationTypes"
                >
                  <legend
                    for="applicationTypeId"
                    class="block px-4 mb-2 text-sm font-bold"
                  >
                    Hakemuksen tyyppi
                  </legend>
                </Radio>
              </div>

              <div class="p-4 mb-4">
                <p>
                  Voit hakea hakukriteerejä vastaavia huoneistoja. Kyseiset
                  huoneistot eivät välttämättä ole vapaita. Asuntohakemus on
                  voimassa 3 kuukautta.
                </p>
              </div>

              <div class="p-4 space-y-4" v-if="payload.favorites.length">
                <h3 class="text-base font-bold">Suosikit</h3>
                <div
                  class="grid grid-cols-2 py-3 text-sm border-t space-4 lg:text-base"
                  v-for="favorite in payload.favorites"
                  :key="favorite.id"
                >
                  <div>
                    {{ favorite.streetAddress }}
                  </div>
                  <div>
                    {{ favorite.buildingType }}, {{ favorite.postOffice }}
                  </div>
                  <div class="flex items-center col-span-2">
                    <span v-if="favorite.apartmentType && favorite.area">
                      {{ favorite.apartmentType }}, {{ favorite.area }}
                    </span>
                    <button
                      class="flex items-center justify-center flex-grow-0 flex-shrink-0 w-6 h-6 ml-auto text-white bg-red-500 rounded-full"
                      @click="toggleFavorite(favorite)"
                    >
                      <span>&times;</span>
                    </button>
                  </div>
                </div>
              </div>

              <label class="block px-4 mb-2 text-sm font-bold" for="locationId"
                >Paikkakunta ja kaupunginosat *</label
              >
              <div class="p-4 mb-4 bg-gray-50">
                <Select name="locationId" :options="options.locations" />
                <Checkboxes
                  v-if="availableDistricts.length"
                  class="mt-4"
                  legend="Valitse kaupunginsosat"
                  name="districtIds"
                  :options="availableDistricts"
                />
              </div>

              <div class="grid grid-cols-2 gap-8 p-4 mb-4">
                <div>
                  <span for="size" class="block mb-2 text-sm"
                    >Huoneiston koko m²</span
                  >
                  <div class="grid grid-cols-9">
                    <Input
                      aria-label="Huoneiston minimikoko"
                      class="col-span-4"
                      type="number"
                      name="sizeMin"
                    >
                      <label
                        class="absolute text-xs text-gray-500 -top-3.5"
                        for="sizeMin"
                        >min</label
                      >
                    </Input>
                    <div class="flex items-center justify-center col-span-1">
                      <span>-</span>
                    </div>
                    <Input
                      aria-label="Huoneiston maksimikoko"
                      class="col-span-4"
                      type="number"
                      name="sizeMax"
                    >
                      <label
                        class="absolute text-xs text-gray-500 -top-3.5"
                        for="sizeMax"
                        >max</label
                      >
                    </Input>
                  </div>
                </div>
                <Input label="Vuokra max €/kk" type="number" name="priceMax" />
              </div>

              <div class="grid grid-cols-1 gap-6 p-4 lg:grid-cols-3">
                <Checkboxes
                  class="col-span-1"
                  container-class="grid grid-cols-1 gap-3"
                  name="buildingTypeIds"
                  legend="Talotyyppi"
                  :options="options.buildingTypes"
                />
                <Checkboxes
                  class="col-span-1"
                  container-class="grid grid-cols-2 gap-3"
                  legend="Huoneistotyyppi"
                  name="apartmentTypeIds"
                  :options="options.apartmentTypes"
                />
                <Checkboxes
                  class="col-span-1"
                  container-class="grid grid-cols-1 gap-3"
                  legend="Ominaisuudet"
                  name="featureIds"
                  :options="options.features"
                />
              </div>

              <div class="flex items-center py-4">
                <button
                  class="w-1/2 px-6 py-2 font-bold rounded-l bg-kas-100 lg:rounded text-kas-500 hover:bg-opacity-80 lg:w-auto"
                  @click="selectPage('intro')"
                >
                  Takaisin <span class="hidden lg:inline">ohjeisiin</span>
                </button>

                <button
                  class="w-1/2 px-6 py-2 ml-auto font-bold text-white rounded-r bg-kas-500 lg:rounded hover:bg-opacity-80 lg:w-auto"
                  type="submit"
                >
                  Jatka <span class="hidden lg:inline">tietojen syöttöön</span>
                </button>
              </div>
            </Form>
          </div>

          <div v-if="currentPage === pages.applicantDetails.value">
            <header class="p-8 text-white bg-kas-500">
              <h1 class="text-2xl font-bold">Hakijoiden tiedot</h1>
              <p>
                Täytä hakijoiden ja muiden asumaan tulevien henkilöiden tiedot.
              </p>
            </header>

            <Form
              class="px-4 py-8 bg-gray-50"
              v-slot="{ errors }"
              :validation-schema="applicantDetailsSchema"
              @submit="submitApplicantDetails"
            >
              <div>
                <div
                  class="grid grid-cols-1 gap-6 pb-4 mb-4 border-b lg:grid-cols-2"
                >
                  <div class="p-3 bg-white border rounded">
                    <h2 class="mb-6 text-lg font-bold">1. hakijan tiedot</h2>
                    <div class="flex flex-col space-y-6">
                      <div>
                        <div class="grid grid-cols-2 gap-6 mb-4">
                          <Input
                            label="Etunimet *"
                            :maxlength="100"
                            name="applicants.primary.firstNames"
                          />
                          <Input
                            label="Sukunimi *"
                            :maxlength="100"
                            name="applicants.primary.lastName"
                          />

                          <Radio
                            name="applicants.primary.nationalityId"
                            :options="options.nationality"
                          >
                            <legend
                              for="applicants.primary.nationalityId"
                              class="block mb-2 text-sm"
                            >
                              Kansalaisuus *
                            </legend>
                          </Radio>

                          <Input
                            class="col-span-2"
                            label="Henkilötunnus *"
                            v-if="
                              payload.applicants.primary.nationalityId !== 63
                            "
                            name="applicants.primary.personalNumber"
                            :maxlength="11"
                          />
                          <Input
                            class="col-span-2"
                            label="Henkilötunnus *"
                            v-if="
                              payload.applicants.primary.nationalityId === 63
                            "
                            name="applicants.primary.passportNumber"
                            :maxlength="11"
                          />
                          <Input
                            class="col-span-2"
                            label="Kotipaikkakunta *"
                            :maxlength="50"
                            name="applicants.primary.placeOfResidence"
                          />
                        </div>
                      </div>

                      <div>
                        <h2 class="text-base font-bold">Nykyinen osoite *</h2>

                        <div class="grid grid-cols-2 gap-2 mb-4">
                          <Input
                            class="col-span-2"
                            label="Katuosoite *"
                            :maxlength="100"
                            name="applicants.primary.streetAddress"
                          />
                          <Input
                            label="Postinumero *"
                            :maxlength="20"
                            name="applicants.primary.postalCode"
                          />
                          <Input
                            label="Postitoimipaikka *"
                            :maxlength="100"
                            name="applicants.primary.city"
                          />
                        </div>
                      </div>

                      <div>
                        <h2 class="text-base font-bold">Yhteystiedot *</h2>

                        <div class="grid grid-cols-2 gap-2">
                          <Input
                            label="Matkapuhelin *"
                            :maxlength="50"
                            name="applicants.primary.phoneNumber"
                          />
                          <Input
                            :label="
                              payload.offerViaEmail
                                ? 'Sähköpostiosoite *'
                                : 'Sähköpostiosoite'
                            "
                            :maxlength="100"
                            name="applicants.primary.emailAddress"
                          />
                        </div>
                      </div>

                      <div>
                        <p>
                          Lähetämme asuntotarjouksen sähköpostilla ja/tai
                          tekstiviestillä.
                        </p>
                        <!--
                        <h2 class="text-base font-bold">Lähetä tarjous</h2>

                        <div class="grid grid-cols-2 gap-2">
                          <Checkbox label="Tekstiviestillä" name="offerViaSms"/>
                          <Checkbox label="Sähköpostilla" name="offerViaEmail"/>
                        </div>
                        -->
                      </div>
                    </div>
                  </div>

                  <div class="p-3 bg-white border rounded">
                    <h2 class="flex items-center mb-6 text-lg font-bold">
                      <span class="flex-1">2. hakijan tiedot</span>
                      <Checkbox
                        container-class="flex-grow-0 flex-shrink-0"
                        label="Täytä toisen hakijan tiedot"
                        name="applicants.secondary.enabled"
                      />
                    </h2>

                    <div class="relative">
                      <div
                        class="absolute z-[50] top-0 bottom-0 left-0 right-0 bg-white bg-opacity-80"
                        v-if="!payload.applicants.secondary.enabled"
                      ></div>
                      <div class="flex flex-col space-y-6">
                        <div class="grid grid-cols-2 gap-6 mb-4">
                          <Input
                            label="Etunimet *"
                            :maxlength="100"
                            name="applicants.secondary.firstNames"
                            :disabled="!payload.applicants.secondary.enabled"
                          />
                          <Input
                            label="Sukunimi *"
                            :maxlength="100"
                            name="applicants.secondary.lastName"
                            :disabled="!payload.applicants.secondary.enabled"
                          />
                          <Radio
                            name="applicants.secondary.nationalityId"
                            :options="options.nationality"
                            :disabled="!payload.applicants.secondary.enabled"
                          >
                            <legend
                              for="applicants.secondary.nationalityId"
                              class="block mb-2 text-sm"
                            >
                              Kansalaisuus *
                            </legend>
                          </Radio>
                          <Input
                            class="col-span-2"
                            label="Henkilötunnus *"
                            v-if="
                              payload.applicants.secondary.nationalityId !== 63
                            "
                            :disabled="!payload.applicants.secondary.enabled"
                            name="applicants.secondary.personalNumber"
                            :maxlength="11"
                          />
                          <Input
                            class="col-span-2"
                            label="Henkilötunnus *"
                            :disabled="!payload.applicants.secondary.enabled"
                            v-if="
                              payload.applicants.secondary.nationalityId === 63
                            "
                            name="applicants.secondary.passportNumber"
                            :maxlength="11"
                          />
                          <Input
                            class="col-span-2"
                            label="Kotipaikkakunta *"
                            :maxlength="50"
                            name="applicants.secondary.placeOfResidence"
                            :disabled="!payload.applicants.secondary.enabled"
                          />
                        </div>
                        <div class="flex">
                          <div class="flex-grow-0 flex-shrink-0">
                            <Checkbox
                              container-class="py-2 mb-4"
                              name="applicants.secondary.livingWithPrimary"
                              :disabled="!payload.applicants.secondary.enabled"
                            >
                              <div class="flex-1">
                                <span
                                  for="applicants.secondary.livingWithPrimary"
                                  class="block text-base font-bold"
                                  >Asun hakijan kanssa tällä hetkellä</span
                                >
                                <p class="text-xs">
                                  Vain jos osoite sama kuin hakijalla
                                </p>
                              </div>
                            </Checkbox>
                          </div>
                          <!-- <div class="flex-1">
                            <span
                              for="applicants.secondary.livingWithPrimary"
                              class="block text-base font-bold"
                              >Asun hakijan kanssa tällä hetkellä</span
                            >
                            <p class="text-xs">
                              Vain jos osoite sama kuin hakijalla
                            </p>
                          </div> -->
                        </div>
                        <div
                          v-if="!payload.applicants.secondary.livingWithPrimary"
                        >
                          <h2 class="text-base font-bold">Nykyinen osoite *</h2>

                          <div class="grid grid-cols-2 gap-2 mb-4">
                            <Input
                              class="col-span-2"
                              label="Katuosoite *"
                              :maxlength="100"
                              name="applicants.secondary.streetAddress"
                              :disabled="!payload.applicants.secondary.enabled"
                            />
                            <Input
                              label="Postinumero *"
                              :maxlength="20"
                              name="applicants.secondary.postalCode"
                              :disabled="!payload.applicants.secondary.enabled"
                            />
                            <Input
                              label="Postitoimipaikka *"
                              :maxlength="100"
                              name="applicants.secondary.city"
                              :disabled="!payload.applicants.secondary.enabled"
                            />
                          </div>
                        </div>
                        <div>
                          <h2 class="text-base font-bold">Yhteystiedot *</h2>
                          <div class="grid grid-cols-2 gap-2">
                            <Input
                              label="Matkapuhelin *"
                              :maxlength="50"
                              name="applicants.secondary.phoneNumber"
                              :disabled="!payload.applicants.secondary.enabled"
                            />
                            <Input
                              label="Sähköpostiosoite"
                              :maxlength="100"
                              name="applicants.secondary.emailAddress"
                              :disabled="!payload.applicants.secondary.enabled"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <h2 class="flex items-center mb-4 text-lg font-bold">
                  <span>Muut asumaan tulevat henkilöt</span>

                  <button
                    class="px-4 py-1 ml-auto text-sm font-bold text-white rounded bg-kas-500 hover:bg-opacity-80"
                    type="button"
                    @click.prevent="addOtherApplicant()"
                  >
                    Lisää henkilö
                  </button>
                </h2>

                <div
                  class="mb-4"
                  v-for="(applicant, i) in payload.applicants.other"
                  :key="i"
                >
                  <div
                    class="grid items-end grid-cols-10 gap-4 p-2 bg-white border"
                  >
                    <div
                      class="grid grid-cols-2 col-span-9 gap-4 lg:grid-cols-4"
                    >
                      <Input
                        label="Etunimet *"
                        :maxlength="100"
                        :name="`applicants.other.${i}.firstNames`"
                      />
                      <Input
                        label="Sukunimi *"
                        :maxlength="100"
                        :name="`applicants.other.${i}.lastName`"
                      />
                      <Radio
                        :name="`applicants.other.${i}.nationalityId`"
                        :options="options.nationality"
                      >
                        <legend
                          :for="`applicants.other.${i}.nationalityId`"
                          class="block mb-2 text-sm"
                        >
                          Kansalaisuus *
                        </legend>
                      </Radio>
                      <Input
                        label="Henkilötunnus *"
                        v-if="applicant.nationalityId !== 63"
                        :name="`applicants.other.${i}.personalNumber`"
                      />
                      <Input
                        label="Henkilötunnus *"
                        v-if="applicant.nationalityId === 63"
                        :name="`applicants.other.${i}.passportNumber`"
                      />
                    </div>
                    <div class="col-span-1 pb-2">
                      <button
                        class="flex items-center justify-center flex-grow-0 flex-shrink-0 w-6 h-6 ml-auto text-white bg-red-500 rounded-full"
                        @click="removeOtherApplicant(i)"
                      >
                        <span>&times;</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="pt-4 border-t">
                  <Checkbox
                    container-class="py-2"
                    label="Raskaustodistus"
                    name="proofOfPregnancy.enabled"
                  />
                  <DateInput
                    v-if="payload.proofOfPregnancy.enabled"
                    class="w-[160px]"
                    label="Laskettu aika"
                    name="proofOfPregnancy.dueDate"
                  />
                </div>
              </div>

              <div class="flex items-center py-4">
                <button
                  class="w-1/2 px-6 py-2 font-bold rounded-l bg-kas-100 lg:rounded text-kas-500 hover:bg-opacity-80 lg:w-auto"
                  @click="selectPage('apartmentDetails')"
                >
                  Takaisin
                  <span class="hidden lg:inline">haettavaan kohteeseen</span>
                </button>

                <button
                  class="w-1/2 px-6 py-2 ml-auto font-bold text-white rounded-r bg-kas-500 lg:rounded hover:bg-opacity-80 lg:w-auto"
                  type="submit"
                >
                  Jatka <span class="hidden lg:inline">tietojen syöttöön</span>
                </button>
              </div>
            </Form>
          </div>

          <div v-if="currentPage === pages.necessityDetails.value">
            <header class="p-8 text-white bg-kas-500">
              <h1 class="text-2xl font-bold">Asunnon tarve</h1>
              <p>Kerro miksi tarvitset asuntoa.</p>
            </header>

            <Form
              class="px-4 py-8 bg-gray-50"
              v-slot="{ errors }"
              :validation-schema="necessityDetailsSchema"
              @submit="submitNecessityDetails"
            >
              <div>
                <div
                  class="grid grid-cols-1 gap-6 pb-4 mb-4 border-b lg:grid-cols-2"
                >
                  <div class="p-3 bg-white border rounded">
                    <h2 class="mb-6 text-lg font-bold">1. hakijan tiedot</h2>
                    <div class="flex flex-col space-y-6">
                      <div>
                        <DateInput
                          label="Asunnon tarve alkaen*"
                          name="necessity.primary.startingAt"
                        />
                      </div>

                      <div>
                        <Radio
                          container-class="flex flex-col space-y-2"
                          :options="[
                            {
                              id: 'homelessness',
                              name: 'Minulla ei ole asuntoa',
                            },
                            { id: 'moving', name: 'Minulla on asunto ' },
                            {
                              id: 'relocation',
                              name: 'Muutto paikkakunnalle ',
                            },
                          ]"
                          name="necessity.primary.mode"
                        >
                          <legend
                            for="necessity.primary.mode"
                            class="block text-sm"
                          >
                            Nykyinen asunto
                          </legend>
                        </Radio>
                      </div>

                      <div
                        v-if="payload.necessity.primary.mode === 'homelessness'"
                      >
                        <h2 class="text-base font-bold">Ei asuntoa</h2>

                        <DateInput
                          label="Ei asuntoa alkaen *"
                          name="necessity.primary.homelessness.startingAt"
                        />
                        <Select
                          label="Syy asunnottomuuteen *"
                          name="necessity.primary.homelessness.reason"
                          :options="options.reasonsForHomelessness"
                        />

                        <Input
                          name="necessity.primary.otherDetails"
                          label="Muut syyt"
                        />
                      </div>

                      <div v-if="payload.necessity.primary.mode === 'moving'">
                        <h2 class="text-base font-bold">On asunto</h2>

                        <div class="grid grid-cols-1 gap-4">
                          <Select
                            label="Muuttovelvoite nykyisestä asunnosta *"
                            name="necessity.primary.moving.necessity"
                            :options="options.necessitiesForMove"
                          />
                          <DateInput
                            label="Muutettava asunnosta pois viimeistään (pp.kk.vvvv)"
                            name="necessity.primary.moving.startingAt"
                          />
                          <Select
                            label="Nykyinen asunto on sopimaton, koska *"
                            name="necessity.primary.moving.reasonCurrentApartmentIsUnsuitable"
                            :options="
                              options.reasonsCurrentApartmentIsUnsuitable
                            "
                          />
                          <Input
                            v-if="
                              payload.necessity.primary.moving
                                .reasonCurrentApartmentIsUnsuitable === 190
                            "
                            label="Nykyinen asunto on sopimaton muu syy"
                            :maxlength="500"
                            name="necessity.primary.moving.otherReasonCurrentApartmentIsUnsuitable"
                          />

                          <Input
                            name="necessity.primary.otherDetails"
                            label="Muut syyt"
                          />
                        </div>
                      </div>

                      <div
                        v-if="payload.necessity.primary.mode === 'relocation'"
                      >
                        <h2 class="text-base font-bold">
                          Muutto paikkakunnalle
                        </h2>

                        <Select
                          label="Syy muuttoon *"
                          name="necessity.primary.relocation.reason"
                          :options="options.reasonsForRelocation"
                        />

                        <Input
                          name="necessity.primary.otherDetails"
                          :label="
                            payload.necessity.primary.relocation.reason === 79
                              ? 'Muut syyt *'
                              : 'Muut syyt'
                          "
                        />

                        <div
                          v-if="
                            payload.necessity.primary.relocation.reason === 77
                          "
                          class="grid grid-cols-1 gap-4 pt-4"
                        >
                          <Input
                            label="Työnantaja *"
                            name="necessity.primary.relocation.employer.name"
                          />
                          <Input
                            label="Työpaikan osoite *"
                            name="necessity.primary.relocation.employer.address"
                          />
                          <DateInput
                            label="Työn alkamispäivä *"
                            name="necessity.primary.relocation.employer.startingAt"
                          />
                        </div>

                        <div
                          v-if="
                            payload.necessity.primary.relocation.reason === 78
                          "
                          class="grid grid-cols-1 gap-4 pt-4"
                        >
                          <Input
                            label="Oppilaitos *"
                            name="necessity.primary.relocation.school.name"
                          />
                        </div>
                      </div>

                      <div
                        v-if="
                          ['moving', 'relocation'].indexOf(
                            payload.necessity.primary.mode
                          ) !== -1
                        "
                      >
                        <h2 class="text-base font-bold">
                          Nykyisen asunnon tiedot
                        </h2>

                        <div class="grid grid-cols-1 gap-4">
                          <Input
                            label="Asukkaita kpl *"
                            type="number"
                            name="necessity.primary.currentApartment.numberOfHabitants"
                          />
                          <Input
                            label="Huoneistotyyppi (esim. 1h+kk, 2h+k) *"
                            :maxlength="50"
                            name="necessity.primary.currentApartment.apartmentType"
                          />
                          <Input
                            label="Huoneiston koko m² *"
                            type="number"
                            name="necessity.primary.currentApartment.apartmentSize"
                          />
                          <Radio
                            container-class="grid grid-cols-2 gap-2"
                            :options="options.currentApartmentOwnershipTypes"
                            name="necessity.primary.currentApartment.ownershipTypeId"
                          >
                            <legend
                              for="necessity.primary.currentApartment.ownershipTypeId"
                              class="block text-sm"
                            >
                              Hallintasuhde *
                            </legend>
                          </Radio>

                          <div
                            v-if="
                              [38, 39].indexOf(
                                payload.necessity.primary.currentApartment
                                  .ownershipTypeId
                              ) !== -1
                            "
                            class="grid grid-cols-2 gap-4"
                          >
                            <div class="col-span-2 text-base font-bold">
                              Vuokranantaja
                            </div>
                            <Input
                              label="Nimi *"
                              :maxlength="250"
                              name="necessity.primary.currentApartment.landlord.name"
                            />
                            <Input
                              label="Puhelinnumero"
                              :maxlength="50"
                              name="necessity.primary.currentApartment.landlord.phoneNumber"
                            />
                          </div>

                          <div
                            v-if="
                              payload.necessity.primary.currentApartment
                                .ownershipTypeId === 44
                            "
                          >
                            <Input
                              label="Jos muu hallintasuhde, mikä *"
                              :maxlength="50"
                              name="necessity.primary.currentApartment.otherOwnershipType"
                            />
                          </div>

                          <Input
                            label="Kaikki asumiskustannukset yhteensä €/kk *"
                            type="number"
                            name="necessity.primary.currentApartment.price"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="p-3 bg-white border rounded"
                    v-if="payload.applicants.secondary.enabled"
                  >
                    <h2 class="flex items-center mb-6 text-lg font-bold">
                      <span class="flex-1">2. hakijan tiedot</span>
                    </h2>

                    <div class="relative">
                      <div class="flex flex-col space-y-6">
                        <div>
                          <DateInput
                            label="Asunnon tarve alkaen *"
                            name="necessity.secondary.startingAt"
                          />
                        </div>

                        <div>
                          <Radio
                            container-class="flex flex-col space-y-2"
                            :options="[
                              {
                                id: 'homelessness',
                                name: 'Minulla ei ole asuntoa',
                              },
                              { id: 'moving', name: 'Minulla on asunto ' },
                              {
                                id: 'relocation',
                                name: 'Muutto paikkakunnalle ',
                              },
                            ]"
                            name="necessity.secondary.mode"
                          >
                            <legend
                              for="necessity.primary.mode"
                              class="block text-sm"
                            >
                              Nykyinen asunto
                            </legend>
                          </Radio>
                        </div>

                        <div
                          v-if="
                            payload.necessity.secondary.mode === 'homelessness'
                          "
                        >
                          <h2 class="text-base font-bold">Ei asuntoa</h2>

                          <DateInput
                            label="Ei asuntoa alkaen *"
                            name="necessity.secondary.homelessness.startingAt"
                          />
                          <Select
                            label="Syy asunnottomuuteen *"
                            name="necessity.secondary.homelessness.reason"
                            :options="options.reasonsForHomelessness"
                          />

                          <Input
                            name="necessity.secondary.otherDetails"
                            label="Muut syyt"
                          />
                        </div>

                        <div
                          v-if="payload.necessity.secondary.mode === 'moving'"
                        >
                          <h2 class="text-base font-bold">On asunto</h2>

                          <div class="grid grid-cols-1 gap-4">
                            <Select
                              label="Muuttovelvoite nykyisestä asunnosta *"
                              name="necessity.secondary.moving.necessity"
                              :options="options.necessitiesForMove"
                            />
                            <DateInput
                              label="Muutettava asunnosta pois viimeistään (pp.kk.vvvv)"
                              name="necessity.secondary.moving.startingAt"
                            />
                            <Select
                              label="Nykyinen asunto on sopimaton, koska *"
                              name="necessity.secondary.moving.reasonCurrentApartmentIsUnsuitable"
                              :options="
                                options.reasonsCurrentApartmentIsUnsuitable
                              "
                            />
                            <Input
                              v-if="
                                payload.necessity.secondary.moving
                                  .reasonCurrentApartmentIsUnsuitable === 190
                              "
                              label="Nykyinen asunto on sopimaton muu syy"
                              :maxlength="500"
                              name="necessity.secondary.moving.otherReasonCurrentApartmentIsUnsuitable"
                            />

                            <Input
                              name="necessity.secondary.otherDetails"
                              label="Muut syyt"
                            />
                          </div>
                        </div>

                        <div
                          v-if="
                            payload.necessity.secondary.mode === 'relocation'
                          "
                        >
                          <h2 class="text-base font-bold">
                            Muutto paikkakunnalle
                          </h2>

                          <Select
                            label="Syy muuttoon *"
                            name="necessity.secondary.relocation.reason"
                            :options="options.reasonsForRelocation"
                          />

                          <Input
                            name="necessity.secondary.otherDetails"
                            label="Muut syyt"
                          />

                          <div
                            v-if="
                              payload.necessity.secondary.relocation.reason ===
                              77
                            "
                            class="grid grid-cols-1 gap-4 pt-4"
                          >
                            <Input
                              label="Työnantaja *"
                              name="necessity.secondary.relocation.employer.name"
                            />
                            <Input
                              label="Työpaikan osoite *"
                              name="necessity.secondary.relocation.employer.address"
                            />
                            <DateInput
                              label="Työn alkamispäivä *"
                              name="necessity.secondary.relocation.employer.startingAt"
                            />
                          </div>

                          <div
                            v-if="
                              payload.necessity.secondary.relocation.reason ===
                              78
                            "
                            class="grid grid-cols-1 gap-4 pt-4"
                          >
                            <Input
                              label="Oppilaitos *"
                              name="necessity.secondary.relocation.school.name"
                            />
                          </div>
                        </div>

                        <div
                          v-if="
                            ['moving', 'relocation'].indexOf(
                              payload.necessity.secondary.mode
                            ) !== -1
                          "
                        >
                          <h2 class="text-base font-bold">
                            Nykyisen asunnon tiedot
                          </h2>

                          <div class="grid grid-cols-1 gap-4">
                            <Input
                              label="Asukkaita kpl *"
                              type="number"
                              name="necessity.secondary.currentApartment.numberOfHabitants"
                            />
                            <Input
                              label="Huoneistotyyppi (esim. 1h+kk, 2h+k) *"
                              :maxlength="50"
                              name="necessity.secondary.currentApartment.apartmentType"
                            />
                            <Input
                              label="Huoneiston koko m² *"
                              type="number"
                              name="necessity.secondary.currentApartment.apartmentSize"
                            />
                            <Radio
                              container-class="grid grid-cols-2 gap-2"
                              :options="options.currentApartmentOwnershipTypes"
                              name="necessity.secondary.currentApartment.ownershipTypeId"
                            >
                              <legend
                                for="necessity.secondary.currentApartment.ownershipTypeId"
                                class="block text-sm"
                              >
                                Hallintasuhde *
                              </legend>
                            </Radio>

                            <div
                              v-if="
                                [38, 39].indexOf(
                                  payload.necessity.secondary.currentApartment
                                    .ownershipTypeId
                                ) !== -1
                              "
                              class="grid grid-cols-2 gap-4"
                            >
                              <div class="col-span-2 text-base font-bold">
                                Vuokranantaja
                              </div>
                              <Input
                                label="Nimi *"
                                :maxlength="250"
                                name="necessity.secondary.currentApartment.landlord.name"
                              />
                              <Input
                                label="Puhelinnumero"
                                :maxlength="50"
                                name="necessity.secondary.currentApartment.landlord.phoneNumber"
                              />
                            </div>

                            <div
                              v-if="
                                payload.necessity.secondary.currentApartment
                                  .ownershipTypeId === 44
                              "
                            >
                              <Input
                                label="Jos muu hallintasuhde, mikä *"
                                :maxlength="50"
                                name="necessity.secondary.currentApartment.otherOwnershipType"
                              />
                            </div>

                            <Input
                              label="Kaikki asumiskustannukset yhteensä €/kk *"
                              type="number"
                              name="necessity.secondary.currentApartment.price"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex items-center py-4">
                <button
                  class="w-1/2 px-6 py-2 font-bold rounded-l bg-kas-100 lg:rounded text-kas-500 hover:bg-opacity-80 lg:w-auto"
                  @click="selectPage('applicantDetails')"
                >
                  Takaisin
                  <span class="hidden lg:inline">hakijoiden tietoihin</span>
                </button>

                <button
                  class="w-1/2 px-6 py-2 ml-auto font-bold text-white rounded-r bg-kas-500 lg:rounded hover:bg-opacity-80 lg:w-auto"
                  type="submit"
                >
                  Jatka <span class="hidden lg:inline">tietojen syöttöön</span>
                </button>
              </div>
            </Form>
          </div>

          <div v-if="currentPage === pages.incomeDetails.value">
            <header class="p-8 text-white bg-kas-500">
              <h1 class="text-2xl font-bold">Tulot ja varallisuus</h1>
              <p>Täytä tiedot tuloistasi ja varallisuudestasi.</p>
            </header>

            <Form
              class="px-4 py-8 bg-gray-50"
              v-slot="{ errors }"
              :validation-schema="incomeDetailsSchema"
              @submit="submitIncomeDetails"
            >
              <div>
                <div
                  class="grid grid-cols-1 gap-6 pb-4 mb-4 border-b lg:grid-cols-2"
                >
                  <div class="p-3 bg-white border rounded">
                    <h2 class="mb-6 text-lg font-bold">1. hakijan tiedot</h2>
                    <div class="flex flex-col space-y-6">
                      <div>
                        <div class="grid grid-cols-2 gap-6 mb-4">
                          <Input
                            label="Bruttotulot €/kk *"
                            type="number"
                            name="income.primary.netIncome"
                          />
                          <Input
                            label="Elatusmaksu €/kk"
                            type="number"
                            name="income.primary.alimony"
                          />
                        </div>
                      </div>

                      <div>
                        <h2 class="text-base font-bold">Varallisuus</h2>

                        <Radio
                          container-class="flex space-x-6"
                          :options="[
                            { id: true, name: 'Kyllä' },
                            { id: false, name: 'Ei' },
                          ]"
                          name="income.primary.wealth.enabled"
                        >
                          <legend
                            for="income.primary.wealth.enabled"
                            class="block px-4 mb-2 text-sm font-bold"
                          >
                            Hakijalla on varallisuutta
                          </legend>
                        </Radio>

                        <div
                          v-if="payload.income.primary.wealth.enabled"
                          class="grid grid-cols-1 gap-4 pt-4"
                        >
                          <Checkboxes
                            container-class="grid grid-cols-2 gap-2"
                            legend="Hakija omistaa kokonaan tai osaksi"
                            :options="options.ownedPropertyTypes"
                            name="income.primary.wealth.ownedPropertyTypeIds"
                          />

                          <Checkboxes
                            container-class="grid grid-cols-2 gap-2"
                            legend="Kiinteistöjen/asuntojen käyttö"
                            :options="options.ownedPropertyUses"
                            name="income.primary.wealth.ownedPropertyUseIds"
                          />

                          <Input
                            label="Muu käyttö, mikä"
                            :maxlength="50"
                            name="income.primary.wealth.otherUse"
                          />

                          <Input
                            label="Kiinteistöjen/asuntojen/osakkeiden nykyinen myyntiarvo, tai hakijan osuus tästä (arvio €)"
                            type="number"
                            name="income.primary.wealth.amount"
                          />
                        </div>
                      </div>

                      <div>
                        <h2 class="text-base font-bold">Lainat</h2>

                        <Radio
                          container-class="flex space-x-6"
                          :options="[
                            { id: true, name: 'Kyllä' },
                            { id: false, name: 'Ei' },
                          ]"
                          name="income.primary.debt.enabled"
                        >
                          <legend
                            for="income.primary.debt.enabled"
                            class="block px-4 mb-2 text-sm font-bold"
                          >
                            Hakijalla on asunto-, opinto- tai muuta lainaa.
                          </legend>
                        </Radio>

                        <div
                          v-if="payload.income.primary.debt.enabled"
                          class="grid grid-cols-2 gap-4 pt-4"
                        >
                          <DateInput
                            label="Myöntämispäivä"
                            class="col-span-2"
                            name="income.primary.debt.startingAt"
                          />
                          <Input
                            label="Lainan määrä €"
                            class="col-span-1"
                            type="number"
                            name="income.primary.debt.amount"
                          />
                          <Input
                            label="Takaisinmaksuvuosi *"
                            class="col-span-1"
                            type="number"
                            name="income.primary.debt.repaidYear"
                          />
                          <Input
                            label="Opintolaina € *"
                            class="col-span-1"
                            type="number"
                            name="income.primary.debt.studentLoan"
                          />
                          <Input
                            label="Muut lainat € *"
                            class="col-span-1"
                            type="number"
                            name="income.primary.debt.otherLoan"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="p-3 bg-white border rounded"
                    v-if="payload.applicants.secondary.enabled"
                  >
                    <h2 class="flex items-center mb-6 text-lg font-bold">
                      <span class="flex-1">2. hakijan tiedot</span>
                    </h2>

                    <div class="relative">
                      <div class="flex flex-col space-y-6">
                        <div>
                          <div class="grid grid-cols-2 gap-6 mb-4">
                            <Input
                              label="Bruttotulot €/kk *"
                              type="number"
                              name="income.secondary.netIncome"
                            />
                            <Input
                              label="Elatusmaksu €/kk"
                              type="number"
                              name="income.secondary.alimony"
                            />
                          </div>
                        </div>

                        <div>
                          <h2 class="text-base font-bold">Varallisuus</h2>

                          <Radio
                            container-class="flex space-x-6"
                            :options="[
                              { id: true, name: 'Kyllä' },
                              { id: false, name: 'Ei' },
                            ]"
                            name="income.secondary.wealth.enabled"
                          >
                            <legend
                              for="income.secondary.wealth.enabled"
                              class="block px-4 mb-2 text-sm font-bold"
                            >
                              Hakijalla on varallisuutta
                            </legend>
                          </Radio>

                          <div
                            v-if="payload.income.secondary.wealth.enabled"
                            class="grid grid-cols-1 gap-4 pt-4"
                          >
                            <Checkboxes
                              container-class="grid grid-cols-2 gap-2"
                              legend="Hakija omistaa kokonaan tai osaksi"
                              :options="options.ownedPropertyTypes"
                              name="income.secondary.wealth.ownedPropertyTypeIds"
                            />

                            <Checkboxes
                              container-class="grid grid-cols-2 gap-2"
                              legend="Kiinteistöjen/asuntojen käyttö"
                              :options="options.ownedPropertyUses"
                              name="income.secondary.wealth.ownedPropertyUseIds"
                            />

                            <Input
                              label="Muu käyttö, mikä"
                              :maxlength="50"
                              name="income.secondary.wealth.otherUse"
                            />

                            <Input
                              label="Kiinteistöjen/asuntojen/osakkeiden nykyinen myyntiarvo, tai hakijan osuus tästä (arvio €)"
                              type="number"
                              name="income.secondary.wealth.amount"
                            />
                          </div>
                        </div>

                        <div>
                          <h2 class="text-base font-bold">Lainat</h2>

                          <Radio
                            container-class="flex space-x-6"
                            :options="[
                              { id: true, name: 'Kyllä' },
                              { id: false, name: 'Ei' },
                            ]"
                            name="income.secondary.debt.enabled"
                          >
                            <legend
                              for="income.secondary.debt.enabled"
                              class="block px-4 mb-2 text-sm font-bold"
                            >
                              Hakijalla on asunto-, opinto- tai muuta lainaa.
                            </legend>
                          </Radio>

                          <div
                            v-if="payload.income.secondary.debt.enabled"
                            class="grid grid-cols-2 gap-4 pt-4"
                          >
                            <DateInput
                              label="Myöntämispäivä"
                              class="col-span-2"
                              name="income.secondary.debt.startingAt"
                            />
                            <Input
                              label="Lainan määrä €"
                              class="col-span-1"
                              type="number"
                              name="income.secondary.debt.amount"
                            />
                            <Input
                              label="Takaisinmaksuvuosi *"
                              class="col-span-1"
                              type="number"
                              name="income.secondary.debt.repaidYear"
                            />
                            <Input
                              label="Opintolaina € *"
                              class="col-span-1"
                              type="number"
                              name="income.secondary.debt.studentLoan"
                            />
                            <Input
                              label="Muut lainat € *"
                              class="col-span-1"
                              type="number"
                              name="income.secondary.debt.otherLoan"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex items-center py-4">
                <button
                  class="w-1/2 px-6 py-2 font-bold rounded-l bg-kas-100 lg:rounded text-kas-500 hover:bg-opacity-80 lg:w-auto"
                  @click="selectPage('necessityDetails')"
                >
                  Takaisin
                  <span class="hidden lg:inline">asunnon tarpeeseen</span>
                </button>

                <button
                  class="w-1/2 px-6 py-2 ml-auto font-bold text-white rounded-r bg-kas-500 lg:rounded hover:bg-opacity-80 lg:w-auto"
                  type="submit"
                >
                  Jatka <span class="hidden lg:inline">vahvistukseen</span>
                </button>
              </div>
            </Form>
          </div>

          <div v-if="currentPage === pages.confirmation.value">
            <header class="block p-8 mb-8 text-white print:hidden bg-kas-500">
              <h1 class="text-2xl font-bold">Tarkista ja lähetä</h1>
              <p>Tarkista antamasi tiedot ja kerro muista toivomuksistasi.</p>
            </header>

            <Form
              class="px-8"
              v-slot="{ errors }"
              :validation-schema="confirmationSchema"
              @submit="submitConfirmation"
            >
              <div v-if="finalErrors.length" class="text-red-500">
                <ul class="mb-8">
                  <li v-for="e in finalErrors">
                    <span class="pr-3 font-bold">{{ e }}</span>
                    <button
                      class="underline"
                      type="button"
                      @click="selectPage('apartmentDetails')"
                    >
                      Palaa valintaan
                    </button>
                  </li>
                </ul>
              </div>

              <Summary :payload="payload" :options="options" />

              <div class="pt-6 space-y-6">
                <div>
                  <Input
                    label="Muut toivomukset"
                    :maxlength="5000"
                    name="additionalInformation"
                  />
                </div>

                <div class="block p-4 border print:hidden bg-gray-50">
                  <p class="mb-2 text-2xl font-bold">
                    Vakuutan antamani tiedot oikeiksi ja hyväksyn luottotietojen
                    tarkistamisen.
                  </p>

                  <div class="font-bold tracking-wide uppercase">
                    <div class="mb-8">
                      <Checkbox
                        label="Hakija(t) antavat suostumuksen omasta/alaikäisten lastensa puolesta tällä hakemuksella annettujen henkilötietojen keräämiseen, käsittelyyn ja tallentamiseen KAS:n tietojärjestelmiin *"
                        name="informationHandling"
                      />
                      <p
                        class="mt-2 text-xs text-red-500"
                        v-if="!payload.informationHandling"
                      >
                        Pakollinen tieto
                      </p>
                    </div>

                    <Checkbox
                      label="Olen tutustunut KAS:n rekisteriselosteeseen *"
                      container-class="pt-2"
                      name="acceptTerms"
                    />
                    <p
                      class="mt-2 text-xs text-red-500"
                      v-if="!payload.acceptTerms"
                    >
                      Pakollinen tieto
                    </p>
                  </div>
                </div>

                <!--
                <div class="block font-bold tracking-wide uppercase print:hidden" v-if="payload.applicants.primary.emailAddress">
                  <Checkbox label="Haluan hakemuksesta koosteen sähköpostiin" name="sendEmail" />
                </div>
                -->

                <div class="block print:hidden">
                  <p>
                    Hakemuksen lähettämisen jälkeen saat yhteenvedon
                    hakemuksella ilmoitettuun sähköpostiosoitteeseen. Tarkista
                    myös roskapostikansiosi.
                  </p>
                </div>

                <!--
                <div class="block print:hidden">
                  <p>Jos annetut tiedot muuttuvat, tulee tehdä uusi hakemus. Mikäli voimme tarjota asuntoa, pyydämme toimittamaan tarvittavat liitteet.</p>
                </div>

                <div class="block print:hidden">
                  <p>Hakemusten käsittelyvaiheessa emme vastaa tiedusteluihin. Jos voimme tarjota asuntoa, lähetämme asuntotarjouksen.</p>
                </div>
                -->
              </div>

              <div class="block print:hidden" v-if="submissionError">
                <p
                  class="p-4 my-4 text-white bg-red-500"
                  v-if="
                    submissionError.Message &&
                    submissionError.Message.includes('osapuolivastine')
                  "
                >
                  Hakemuksen henkilötietoja on tarkastettava. Ota hakemuksen
                  käsittelemiseksi yhteyttä
                  <a href="mailto:vuokraus@kas.fi">vuokraus@kas.fi</a> tai
                  vuokrausneuvottelijaan
                  <a href="https://kas.fi/yhteystiedot" target="_blank"
                    >kas.fi/yhteystiedot</a
                  >.
                </p>
                <div
                  class="p-4 my-4 text-white bg-red-500"
                  v-else-if="submissionError.ModelState !== null"
                >
                  <div
                    v-for="key in Object.keys(submissionError.ModelState)"
                    :key="key"
                  >
                    {{ submissionError.ModelState[key][0] ?? "" }}
                  </div>
                </div>
                <div class="p-4 my-4 text-white bg-red-500" v-else>
                  {{ submissionError.Message }}
                </div>
              </div>

              <div class="flex items-center justify-between py-4 print:hidden">
                <button
                  class="px-6 py-2 font-bold rounded bg-kas-100 text-kas-500 hover:bg-opacity-80"
                  @click="selectPage('incomeDetails')"
                >
                  Takaisin
                  <span class="hidden lg:inline"
                    >tuloihin ja varallisuuteen</span
                  >
                </button>
                <span>
                  <button
                    class="px-6 py-2 font-bold rounded bg-kas-100 text-kas-500 hover:bg-opacity-80"
                    @click.prevent="print"
                  >
                    Tulosta hakemus
                  </button>
                  <button
                    class="px-6 py-2 ml-5 font-bold text-white rounded bg-kas-500 hover:bg-opacity-80 disabled:opacity-25 disabled:cursor-not-allowed"
                    :disabled="
                      sending ||
                      !payload.acceptTerms ||
                      !payload.informationHandling ||
                      finalErrors.length
                    "
                    type="submit"
                  >
                    Lähetä
                  </button>
                </span>
              </div>
            </Form>
          </div>
        </div>

        <div class="block print:hidden lg:w-1/4">
          <aside class="px-2 py-8 mt-8 bg-kas-500">
            <h2 class="text-lg font-bold text-white">Eteneminen</h2>
            <nav>
              <div v-for="key in navigablePageKeys" :key="key">
                <button
                  class="flex items-center py-4 space-x-4 text-lg"
                  @click="selectPage(key, true)"
                >
                  <span
                    class="flex items-center justify-center flex-grow-0 flex-shrink-0 w-10 h-10 text-white border border-white rounded-full"
                    :class="
                      currentPage === pages[key].value
                        ? 'bg-green-700'
                        : 'bg-kas-500'
                    "
                  >
                    <span>{{ pages[key].value }}</span>
                  </span>
                  <span
                    class="text-left"
                    :class="
                      currentPage === pages[key].value
                        ? 'text-white'
                        : 'text-kas-300'
                    "
                    >{{ pages[key].label }}</span
                  >
                </button>
              </div>
            </nav>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Form, Field, ErrorMessage } from "vee-validate";
import Checkbox from "../../poo/components/Checkbox";
import Checkboxes from "../../poo/components/Checkboxes";
import DateInput from "../../poo/components/DateInput";
import Input from "../../poo/components/Input";
import Radio from "../../poo/components/Radio";
import Select from "../../poo/components/Select";
import Summary from "./Parts/Summary";
import userValidators from "../../use/validators.js";
import useApplicationOptions from "../../use/application/options.js";
import { computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import { post } from "../../utils";

function includeIf(state, value, defaultValue = {}) {
  if (!state) {
    return defaultValue;
  }

  return value;
}

export default {
  name: "Application",
  components: {
    Form,
    Field,
    ErrorMessage,
    Checkbox,
    Checkboxes,
    DateInput,
    Input,
    Radio,
    Select,
    Summary,
  },
  setup() {
    const pages = {
      intro: {
        value: 0,
        label: "Vuokra-asuntohakemus",
      },
      apartmentDetails: {
        value: 1,
        label: "Haettava kohde",
      },
      applicantDetails: {
        value: 2,
        label: "Hakijoiden tiedot",
      },
      necessityDetails: {
        value: 3,
        label: "Asunnon tarve",
      },
      incomeDetails: {
        value: 4,
        label: "Tulot ja varallisuus",
      },
      confirmation: {
        value: 5,
        label: "Tarkistus ja lähetys",
      },
    };

    onMounted(() => {
      document.title = "Vuokra-asuntohakemus";
    });

    function submitApartmentDetails(values) {
      selectPage("applicantDetails");
    }

    function submitApplicantDetails(values) {
      selectPage("necessityDetails");
    }

    function submitNecessityDetails(values) {
      selectPage("incomeDetails");
    }

    function submitIncomeDetails(values) {
      selectPage("confirmation");
    }

    const sending = ref(false);
    const submissionError = ref(null);
    const submission = ref(null);

    async function submitConfirmation() {
      if (!store.state.acceptTerms || !store.state.informationHandling) {
        return false;
      }

      sending.value = true;
      submissionError.value = null;
      submission.value = null;

      const response = await post("/application", {
        state: store.state,
        payload: store.getters.tampuuriResult,
        meta: {
          navigator: window.navigator.userAgent ?? null,
          page:
            window.localStorage?.getItem("kas_application_current_page") ??
            null,
          result:
            window.localStorage?.getItem("kas_application_result") ?? null,
        },
      });

      if (response?.result?.status === 400) {
        submissionError.value = {
          Message:
            response?.result?.body?.Message ??
            "Hakemuksen lähettäminen ei onnistunut.",
          ModelState: response?.result?.body?.ModelState ?? null,
        };
      }

      if (response?.result?.status === 201) {
        submission.value = response;

        store.commit("forget");
        window.localStorage?.removeItem("kas_application_current_page");
      }

      sending.value = false;
    }

    const navigablePageKeys = Object.keys(pages).filter(
      (key) => key !== "intro"
    );

    const rememberedPage =
      window.localStorage?.getItem("kas_application_current_page") ?? null;

    const currentPage = ref(
      rememberedPage ? Number(rememberedPage) : pages.intro.value
    );

    function selectPage(key, navigate = false) {
      if (pages[key]) {
        if (navigate && currentPage.value <= pages[key].value) {
          return false;
        }

        currentPage.value = pages[key].value;

        window.localStorage?.setItem(
          "kas_application_current_page",
          String(currentPage.value)
        );

        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });

        window.location.reload();
      }
    }

    function print() {
      window.print();
    }

    const store = useStore();
    const validators = userValidators();
    const { options, applicationOptionsIsLoading } = useApplicationOptions();

    const availableDistricts = computed(() => {
      if (!store.state.locationId || !options.value) {
        return [];
      }

      return options.value.districts.filter(
        ({ locationId }) => locationId === store.state.locationId
      );
    });

    const finalErrors = computed(() => {
      return [
        !store.state.locationId ? "Kaupunkivalinta puuttuu" : false,
      ].filter((item) => item !== false);
    });

    const apartmentDetailsSchema = computed(() => {
      return {
        locationId: validators.required,
        sizeMin: validators.numeric,
        sizeMax: validators.numeric,
        priceMax: validators.numeric,
        featureIds: validators.requiredNonEmpty,
      };
    });

    const applicantDetailsSchema = computed(() => {
      const { offerViaEmail, applicants } = store.state;

      let otherRules = {};

      applicants.other.forEach((applicant, i) => {
        otherRules = {
          ...otherRules,
          ...{
            [`applicants.other.${i}.firstNames`]: validators.required,
            [`applicants.other.${i}.lastName`]: validators.required,
            [`applicants.other.${i}.nationalityId`]: validators.required,
            ...includeIf(applicant.nationalityId === 62, {
              [`applicants.other.${i}.personalNumber`]:
                validators.requiredPersonalNumber,
            }),
            ...includeIf(applicant.nationalityId === 63, {
              [`applicants.other.${i}.passportNumber`]:
                validators.requiredPersonalNumber,
            }),
          },
        };
      });

      return {
        "applicants.primary.firstNames": validators.required,
        "applicants.primary.lastName": validators.required,
        "applicants.primary.nationalityId": validators.required,
        ...includeIf(applicants.primary.nationalityId === 62, {
          "applicants.primary.personalNumber":
            validators.requiredPersonalNumber,
        }),
        ...includeIf(applicants.primary.nationalityId === 63, {
          "applicants.primary.passportNumber":
            validators.requiredPersonalNumber,
        }),
        "applicants.primary.placeOfResidence": validators.required,
        "applicants.primary.streetAddress": validators.required,
        "applicants.primary.postalCode": validators.requiredPostalCode,
        "applicants.primary.city": validators.required,
        "applicants.primary.phoneNumber": validators.required,
        "applicants.primary.emailAddress": includeIf(
          offerViaEmail,
          validators.requiredEmail,
          validators.email
        ),
        ...includeIf(applicants.secondary.enabled, {
          "applicants.secondary.firstNames": validators.required,
          "applicants.secondary.lastName": validators.required,
          "applicants.secondary.nationalityId": validators.required,
          ...includeIf(applicants.secondary.nationalityId === 62, {
            "applicants.secondary.personalNumber":
              validators.requiredPersonalNumber,
          }),
          ...includeIf(applicants.secondary.nationalityId === 63, {
            "applicants.secondary.passportNumber":
              validators.requiredPersonalNumber,
          }),
          "applicants.secondary.placeOfResidence": validators.required,
          ...includeIf(!applicants.secondary.livingWithPrimary, {
            "applicants.secondary.streetAddress": validators.required,
            "applicants.secondary.postalCode": validators.requiredPostalCode,
            "applicants.secondary.city": validators.required,
          }),
          "applicants.secondary.phoneNumber": validators.required,
          "applicants.secondary.emailAddress": validators.email,
        }),
        ...otherRules,
      };
    });

    const necessityDetailsSchema = computed(() => {
      const { applicants, necessity } = store.state;

      return {
        "necessity.primary.startingAt": validators.required,
        "necessity.primary.mode": validators.required,
        ...includeIf(
          necessity.primary.mode === "relocation" &&
            necessity.primary.relocation.reason === 79,
          {
            "necessity.primary.otherDetails": validators.requiredMinLength(5),
          },
          {
            "necessity.primary.otherDetails": validators.nullable,
          }
        ),
        ...includeIf(necessity.primary.mode === "homelessness", {
          "necessity.primary.homelessness.startingAt": validators.required,
          "necessity.primary.homelessness.reason": validators.required,
        }),
        ...includeIf(necessity.primary.mode === "moving", {
          "necessity.primary.moving.startingAt": validators.required,
          "necessity.primary.moving.necessity": validators.required,
          "necessity.primary.moving.reasonCurrentApartmentIsUnsuitable":
            validators.required,
          ...includeIf(
            necessity.primary.moving.reasonCurrentApartmentIsUnsuitable === 190,
            {
              "necessity.primary.moving.otherReasonCurrentApartmentIsUnsuitable":
                validators.required,
            }
          ),
        }),
        ...includeIf(necessity.primary.mode === "relocation", {
          "necessity.primary.relocation.reason": validators.required,
          // 'necessity.primary.moving.reasonCurrentApartmentIsUnsuitable': validators.required,
          ...includeIf(necessity.primary.relocation.reason === 77, {
            "necessity.primary.relocation.employer.name": validators.required,
            "necessity.primary.relocation.employer.address":
              validators.required,
            "necessity.primary.relocation.employer.startingAt":
              validators.required,
          }),
          ...includeIf(necessity.primary.relocation.reason === 78, {
            "necessity.primary.relocation.school.name": validators.required,
          }),
        }),
        ...includeIf(
          ["moving", "relocation"].indexOf(necessity.primary.mode) !== -1,
          {
            "necessity.primary.currentApartment.numberOfHabitants":
              validators.requiredNumeric,
            "necessity.primary.currentApartment.apartmentType":
              validators.required,
            "necessity.primary.currentApartment.apartmentSize":
              validators.requiredNumeric,
            "necessity.primary.currentApartment.ownershipTypeId":
              validators.required,
            ...includeIf(
              necessity.primary.currentApartment.ownershipTypeId === 44,
              {
                "necessity.primary.currentApartment.otherOwnershipType":
                  validators.required,
              }
            ),
            "necessity.primary.currentApartment.price":
              validators.requiredNumeric,
            ...includeIf(
              [38, 39].indexOf(
                necessity.primary.currentApartment.ownershipTypeId
              ) !== -1,
              {
                "necessity.primary.currentApartment.landlord.name":
                  validators.required,
                // 'necessity.primary.currentApartment.landlord.phoneNumber': validators.required,
              }
            ),
          }
        ),

        ...includeIf(applicants.secondary.enabled, {
          "necessity.secondary.startingAt": validators.required,
          "necessity.secondary.mode": validators.required,
          ...includeIf(
            necessity.secondary.mode === "relocation" &&
              necessity.secondary.relocation.reason === 79,
            {
              "necessity.secondary.otherDetails": validators.required,
            },
            {
              "necessity.secondary.otherDetails": validators.nullable,
            }
          ),
          ...includeIf(necessity.secondary.mode === "homelessness", {
            "necessity.secondary.homelessness.startingAt": validators.required,
            "necessity.secondary.homelessness.reason": validators.required,
          }),
          ...includeIf(necessity.secondary.mode === "moving", {
            "necessity.secondary.moving.startingAt": validators.required,
            "necessity.secondary.moving.necessity": validators.required,
            "necessity.secondary.moving.reasonCurrentApartmentIsUnsuitable":
              validators.required,
            ...includeIf(
              necessity.secondary.moving.reasonCurrentApartmentIsUnsuitable ===
                190,
              {
                "necessity.secondary.moving.otherReasonCurrentApartmentIsUnsuitable":
                  validators.required,
              }
            ),
          }),
          ...includeIf(necessity.secondary.mode === "relocation", {
            "necessity.secondary.relocation.reason": validators.required,
            // 'necessity.secondary.moving.reasonCurrentApartmentIsUnsuitable': validators.required,
            ...includeIf(necessity.secondary.relocation.reason === 77, {
              "necessity.secondary.relocation.employer.name":
                validators.required,
              "necessity.secondary.relocation.employer.address":
                validators.required,
              "necessity.secondary.relocation.employer.startingAt":
                validators.required,
            }),
            ...includeIf(necessity.secondary.relocation.reason === 78, {
              "necessity.secondary.relocation.school.name": validators.required,
            }),
          }),
          ...includeIf(
            ["moving", "relocation"].indexOf(necessity.secondary.mode) !== -1,
            {
              "necessity.secondary.currentApartment.numberOfHabitants":
                validators.requiredNumeric,
              "necessity.secondary.currentApartment.apartmentType":
                validators.required,
              "necessity.secondary.currentApartment.apartmentSize":
                validators.requiredNumeric,
              "necessity.secondary.currentApartment.ownershipTypeId":
                validators.required,
              ...includeIf(
                necessity.secondary.currentApartment.ownershipTypeId === 44,
                {
                  "necessity.secondary.currentApartment.otherOwnershipType":
                    validators.required,
                }
              ),
              "necessity.secondary.currentApartment.price":
                validators.requiredNumeric,
              ...includeIf(
                [38, 39].indexOf(
                  necessity.secondary.currentApartment.ownershipTypeId
                ) !== -1,
                {
                  "necessity.secondary.currentApartment.landlord.name":
                    validators.required,
                  // 'necessity.secondary.currentApartment.landlord.phoneNumber': validators.required,
                }
              ),
            }
          ),
        }),
      };
    });

    const incomeDetailsSchema = computed(() => {
      const { applicants, income } = store.state;

      return {
        "income.primary.netIncome": validators.requiredNumeric,
        "income.primary.alimony": validators.numeric,
        "income.primary.wealth.enabled": validators.required,
        ...includeIf(income.primary.wealth.enabled, {
          "income.primary.wealth.amount": validators.requiredNumeric,
        }),
        "income.primary.debt.enabled": validators.required,
        ...includeIf(income.primary.debt.enabled, {
          "income.primary.debt.startingAt": validators.required,
          "income.primary.debt.amount": validators.requiredNumeric,
          "income.primary.debt.repaidYear": validators.requiredNumeric,
          "income.primary.debt.studentLoan": validators.requiredNumeric,
          "income.primary.debt.otherLoan": validators.requiredNumeric,
        }),

        ...includeIf(applicants.secondary.enabled, {
          "income.secondary.netIncome": validators.requiredNumeric,
          "income.secondary.alimony": validators.numeric,
          "income.secondary.wealth.enabled": validators.required,
          ...includeIf(income.secondary.wealth.enabled, {
            "income.secondary.wealth.amount": validators.requiredNumeric,
          }),
          "income.secondary.debt.enabled": validators.required,
          ...includeIf(income.secondary.debt.enabled, {
            "income.secondary.debt.startingAt": validators.required,
            "income.secondary.debt.amount": validators.requiredNumeric,
            "income.secondary.debt.repaidYear": validators.requiredNumeric,
            "income.secondary.debt.studentLoan": validators.requiredNumeric,
            "income.secondary.debt.otherLoan": validators.requiredNumeric,
          }),
        }),
      };
    });

    const confirmationSchema = computed(() => {
      return {
        additionalInformation: validators.nullable,
        acceptTerms: validators.required,
        informationHandling: validators.required,
      };
    });

    function toggleFavorite(favorite) {
      store.commit("toggleFavorite", favorite);
    }

    return {
      finalErrors,
      pages,
      currentPage,
      selectPage,
      navigablePageKeys,
      payload: store.state,
      result: computed(() => store.getters.tampuuriResult),

      addOtherApplicant: () => store.commit("addOtherApplicant"),
      removeOtherApplicant: (i) => store.commit("removeOtherApplicant", i),

      apartmentDetailsSchema,
      applicantDetailsSchema,
      necessityDetailsSchema,
      incomeDetailsSchema,
      confirmationSchema,

      submitApartmentDetails,
      submitApplicantDetails,
      submitNecessityDetails,
      submitIncomeDetails,
      submitConfirmation,

      validators,
      availableDistricts,
      options,
      toggleFavorite,
      print,

      submissionError,
      submission,
      sending,
    };
  },
};
</script>
